import type {
  ApiEndpoints,
  DismissSupervisorFindingRequest,
  DocumentRetryRunner,
  DocumentSchema,
  ProjectOpenItemGroupKey,
  ProjectUndeliveredReason,
  SupervisorAuditLabelRequest,
} from '@avisoma/shared';
import axios, { type AxiosRequestConfig } from 'axios';
import { globalToast } from '../components/Toast';
import { isSilentLoginUnauthorized, type AuthRequestConfig } from '../lib/authSession';

/** Per-request opt-out from the interceptor's generic error toast, for callers that toast failures themselves. */
export type ToastRequestConfig = { skipErrorToast?: boolean };

const SKIP_ERROR_TOAST = { skipErrorToast: true } as AxiosRequestConfig & ToastRequestConfig;

export interface ServerStatus {
  version: string;
  build: {
    date: string;
    id: string;
  };
  environment: string;
  configProfile: string;
  embeddings: {
    provider: string;
    model: string | undefined;
    host: string | undefined;
  };
  features: {
    llmPageParser: {
      enabled: boolean;
      model: string;
      baseUrl: string;
    };
    topics: boolean;
    bridgePythonScripts: boolean;
    training: boolean;
  };
  /** Per-category extraction pipeline and model, derived from the extraction configs. */
  extraction: {
    types: {
      category: string;
      pipeline: 'single' | 'agentic';
      model: string;
    }[];
  };
  stats: {
    categories: number;
    documents: number;
    pages: number;
    projects: number;
    organisations: number;
  };
  server: {
    requestTimeoutMs: number;
    useHttp2: boolean;
  };
  cloud: {
    projectId: string;
    geminiVertexLocation: string;
    geminiClientMode: 'vertex' | 'apikey';
    buckets: {
      documents: string;
      pages: string;
      reports: string;
    };
  };
  postgres: {
    uri: string;
  };
}

class Backend {
  constructor() {
    // Handle 401 responses
    axios.interceptors.request.use((config) => {
      (config as AuthRequestConfig).requestPathname = window.location.pathname;
      return config;
    });
    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        const requestPathname = axios.isAxiosError(error)
          ? (error.config as AuthRequestConfig | undefined)?.requestPathname
          : undefined;
        if (error.response?.status === 401 && isSilentLoginUnauthorized(requestPathname)) {
          return Promise.reject(error);
        }
        if (error.response?.status === 401 && this.notifyUnauthorized()) {
          return;
        }

        if ((error.config as ToastRequestConfig | undefined)?.skipErrorToast) {
          return Promise.reject(error);
        }

        const errgr = error instanceof Error ? error.message : `Unknown error`;
        const message = axios.isAxiosError(error)
          ? (error.response?.data as { error?: { message?: string } } | undefined)?.error?.message
          : undefined;
        globalToast.error(`${errgr}: ${message}`);

        return Promise.reject(error);
      },
    );
  }

  /**
   * The 401 handoff to `AuthUnauthorizedListener`. Raw `fetch` callers (streams) call it too,
   * since they bypass the axios interceptor. False on /login, where nothing fires.
   */
  notifyUnauthorized(): boolean {
    if (window.location.pathname === '/login') return false;
    localStorage.removeItem('token');
    window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { redirect: window.location.pathname } }));
    return true;
  }

  setCsrfToken(token: string) {
    axios.defaults.headers.common['X-CSRF-Token'] = token;
  }

  getCsrfToken(): string | undefined {
    const value = axios.defaults.headers.common['X-CSRF-Token'];
    return typeof value === 'string' ? value : undefined;
  }

  clearAuth() {
    delete axios.defaults.headers.common['X-CSRF-Token'];
  }

  async getPagesDistinctCategories() {
    return axios.get<ApiEndpoints<string>['/api/document-pages/distinct-categories']['GET']['response']>(
      '/api/document-pages/distinct-categories',
    );
  }

  async getPagesDistinctTags() {
    return axios.get<ApiEndpoints<string>['/api/document-pages/distinct-tags']['GET']['response']>(
      '/api/document-pages/distinct-tags',
    );
  }

  async getClassificationWeekly(query?: { weeks?: number }) {
    return axios.get<ApiEndpoints<string>['/api/aggregates/classification-weekly']['GET']['response']>(
      '/api/aggregates/classification-weekly',
      {
        params: query?.weeks != null ? { weeks: query.weeks } : undefined,
      },
    );
  }

  async listDocuments(
    page = 1,
    limit = 10,
    filters?: {
      search?: string;
      category?: string | null;
      status?: 'done' | 'pending' | 'error' | 'invalid' | '';
      classification?: string | null;
      classificationScore?: string;
      tags?: string[];
      uploadId?: string;
      ids?: string[];
      onlyIds?: string[];
      projectId?: string;
      attention?: boolean;
    },
  ) {
    return axios.get<ApiEndpoints<string>['/api/documents']['GET']['response']>('/api/documents', {
      params: {
        page,
        limit,
        ...(filters?.search && { search: filters.search }),
        ...(filters?.projectId && { projectId: filters.projectId }),
        ...(filters?.category && { category: filters.category }),
        ...(filters?.status && { status: filters?.status }),
        ...(filters?.classification && {
          classification: filters?.classification,
        }),
        ...(filters?.tags && { tags: filters?.tags }),
        ...(filters?.uploadId && { uploadId: filters?.uploadId }),
        ...(filters?.ids && { ids: filters?.ids }),
        ...(filters?.onlyIds && { onlyIds: filters?.onlyIds }),
        ...(filters?.classificationScore && {
          classificationScore: filters?.classificationScore,
        }),
        ...(filters?.attention && { attention: '1' }),
      },
    });
  }

  async listDocumentPages(
    page = 1,
    limit = 10,
    filters?: {
      search?: string;
      category?: string | null;
      status?: 'done' | 'pending' | 'error' | 'invalid' | '';
      classification?: string | null;
      classificationScore?: string;
      tags?: string[];
      projectId?: string;
    },
  ) {
    return axios.get<ApiEndpoints<string>['/api/document-pages']['GET']['response']>('/api/document-pages', {
      params: {
        page,
        limit,
        ...(filters?.search && { search: filters.search }),
        ...(filters?.projectId && { projectId: filters.projectId }),
        ...(filters?.category && { category: filters.category }),
        ...(filters?.status && { status: filters?.status }),
        ...(filters?.tags && { tags: filters?.tags }),
        ...(filters?.classification && {
          classification: filters?.classification,
        }),
        ...(filters?.classificationScore && {
          classificationScore: filters?.classificationScore,
        }),
      },
    });
  }

  async getDocument(id: string) {
    return axios.get<ApiEndpoints<string>['/api/documents/:id']['GET']['response']>(`/api/documents/${id}`);
  }

  async listDocumentReconstructedDocuments(
    id: string,
    query?: ApiEndpoints<string>['/api/documents/:id/reconstructed-documents']['GET']['query'],
  ) {
    return axios.get<ApiEndpoints<string>['/api/documents/:id/reconstructed-documents']['GET']['response']>(
      `/api/documents/${id}/reconstructed-documents`,
      { params: query },
    );
  }

  async getDocumentPages(id: string) {
    return axios.get<ApiEndpoints<string>['/api/documents/:id/pages']['GET']['response']>(`/api/documents/${id}/pages`);
  }

  async getDocumentPage(id: string) {
    return axios.get<ApiEndpoints<string>['/api/document-pages/:id']['GET']['response']>(`/api/document-pages/${id}`);
  }

  async getDocumentPageHtml(id: string) {
    return axios.get<ApiEndpoints<string>['/api/document-pages/:id/html']['GET']['response']>(
      `/api/document-pages/${id}/html`,
    );
  }

  async locateDocumentPageText(
    id: string,
    body: ApiEndpoints<string>['/api/document-pages/:id/text-locations']['POST']['body'],
    query?: ApiEndpoints<string>['/api/document-pages/:id/text-locations']['POST']['query'],
  ) {
    return axios.post<ApiEndpoints<string>['/api/document-pages/:id/text-locations']['POST']['response']>(
      `/api/document-pages/${id}/text-locations`,
      body,
      { params: query },
    );
  }

  async listReconstructedDocuments(
    query: ApiEndpoints<string>['/api/reconstructed-documents']['GET']['query'],
  ) {
    return axios.get<ApiEndpoints<string>['/api/reconstructed-documents']['GET']['response']>(
      '/api/reconstructed-documents',
      { params: query },
    );
  }

  async getReconstructedDocument(id: string) {
    return axios.get<ApiEndpoints<string>['/api/reconstructed-documents/:id']['GET']['response']>(
      `/api/reconstructed-documents/${id}`,
    );
  }

  async patchReconstructedDocument(
    id: string,
    data: ApiEndpoints<string>['/api/reconstructed-documents/:id']['PATCH']['body'],
  ) {
    return axios.patch<ApiEndpoints<string>['/api/reconstructed-documents/:id']['PATCH']['response']>(
      `/api/reconstructed-documents/${id}`,
      data,
    );
  }

  async reextractReconstructedDocument(id: string) {
    return axios.post<ApiEndpoints<string>['/api/reconstructed-documents/:id/re-extract']['POST']['response']>(
      `/api/reconstructed-documents/${id}/re-extract`,
      {},
    );
  }

  async uploadDocuments(
    {
      files,
      category,
      url,
      tags,
      projectId,
      webhookUrl,
      urls,
      topic,
      organisationId,
      organisationName,
    }: {
      files?: File[];
      category?: string;
      url?: string;
      tags?: string[];
      projectId?: string;
      webhookUrl?: string;
      topic?: string;
      urls?: string[];
      organisationId?: string;
      organisationName?: string;
    },
    onProgress?: (progress: {
      progress: number;
      uploaded: number;
      total: number;
      speedBps: number;
      speedKbps: number;
      speedMbps: number;
      etaSec: number;
    }) => void,
  ) {
    const formData = new FormData();
    files?.forEach((file) => {
      formData.append('files', file);
    });
    if (category) formData.append('category', category);
    if (url) formData.append('url', url);
    urls?.forEach((url) => formData.append('urls[]', url));
    tags?.forEach((tag) => formData.append('tags[]', tag));

    if (projectId) formData.append('projectId', projectId);
    if (webhookUrl) formData.append('webhookUrl', webhookUrl);
    if (topic) formData.append('topic', topic);
    if (organisationId) formData.append('organisationId', organisationId);
    if (organisationName) formData.append('organisationName', organisationName);
    // formData.append('forceProcessing', 'true');

    const startTime = Date.now();

    return axios.post<ApiEndpoints<string>['/api/documents']['POST']['response']>('/api/documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (event) => {
        if (event.total) {
          const uploaded = event.loaded;
          const total = event.total;
          const progress = (uploaded / total) * 100;

          const elapsedSec = (Date.now() - startTime) / 1000;
          const speedBps = uploaded / elapsedSec;
          const speedKbps = speedBps / 1024;
          const speedMbps = speedKbps / 1024;

          const remainingBytes = total - uploaded;
          const etaSec = remainingBytes / speedBps;

          const progressData = {
            progress, // %
            uploaded, // bytes
            total, // bytes
            speedBps, // bytes/sec
            speedKbps,
            speedMbps,
            etaSec, // seconds remaining
          };

          onProgress?.(progressData);
        }
      },
    });
  }


  async getDocumentsDistinctCategories() {
    const response =
      await axios.get<ApiEndpoints<string>['/api/documents/categories']['GET']['response']>(
        '/api/documents/categories',
      );
    return response.data;
  }

  async deleteDocument(id: string) {
    return axios.delete<ApiEndpoints<string>['/api/documents/:id']['DELETE']['response']>(`/api/documents/${id}`);
  }

  async getDocumentCascadePreview(id: string) {
    return axios.get<ApiEndpoints<string>['/api/documents/:id/cascade-preview']['GET']['response']>(
      `/api/documents/${id}/cascade-preview`,
    );
  }

  // The caller toasts the server's reason itself (DR-6226: 409 when the page is shared).
  async deleteDocumentPage(id: string) {
    return axios.delete<ApiEndpoints<string>['/api/document-pages/:id']['DELETE']['response']>(
      `/api/document-pages/${id}`,
      SKIP_ERROR_TOAST,
    );
  }

  async retryDocument(
    id: string,
    runner: 'pdf-parser' | 'embeddings' | 'classification' | 'reconstruction' | 'pdf-splitter' | 'metadata' | 'data-extraction',
    options?: { projectId?: string },
  ) {
    return axios.post<ApiEndpoints<string>['/api/documents/:id/retry']['POST']['response']>(`/api/documents/${id}/retry`, {
      runner,
      ...(options?.projectId != null && options.projectId !== ''
        ? { projectId: options.projectId }
        : {}),
    });
  }

  /**
   * Bulk sibling of {@link retryDocument}: one request retries the whole set and the
   * server fans out to its own queue, instead of the browser firing a request per
   * document. Resolves once the retries are enqueued, not once they have run.
   */
  async bulkRetryDocuments(documentIds: string[], runner: DocumentRetryRunner, options?: { projectId?: string }) {
    return axios.post<ApiEndpoints<string>['/api/documents/bulk-retry']['POST']['response']>(
      '/api/documents/bulk-retry',
      {
        documentIds,
        runner,
        ...(options?.projectId != null && options.projectId !== '' ? { projectId: options.projectId } : {}),
      },
    );
  }

  async createCategory(name: string) {
    return axios.post<ApiEndpoints<string>['/api/categories']['POST']['response']>('/api/categories', { name });
  }

  async retryAllFailedSteps() {
    return axios.post<ApiEndpoints<string>['/api/documents/retry-all-failed-steps']['POST']['response']>(
      '/api/documents/retry-all-failed-steps',
    );
  }

  async getStrandedPages(id: string) {
    return axios.get<ApiEndpoints<string>['/api/documents/:id/stranded']['GET']['response']>(
      `/api/documents/${id}/stranded`,
    );
  }

  async recoverDocument(id: string) {
    return axios.post<ApiEndpoints<string>['/api/documents/:id/recover']['POST']['response']>(
      `/api/documents/${id}/recover`,
    );
  }

  async getDocumentStats() {
    return axios.get<ApiEndpoints<string>['/api/documents/stats']['GET']['response']>('/api/documents/stats');
  }

  async getPipelineStatus() {
    return axios.get<ApiEndpoints<string>['/api/system/pipeline-status']['GET']['response']>(
      '/api/system/pipeline-status',
    );
  }

  async getPipelineStageErrors(stage: string) {
    return axios.get<ApiEndpoints<string>['/api/system/pipeline-stage-errors']['GET']['response']>(
      '/api/system/pipeline-stage-errors',
      { params: { stage } },
    );
  }

  async getDocumentAttention() {
    return axios.get<ApiEndpoints<string>['/api/documents/attention']['GET']['response']>('/api/documents/attention');
  }

  async getStatus() {
    return axios.get<ServerStatus>('/api/status');
  }

  async resetDocuments(depth: 1 | 2 | 3 | 4, documentIds?: string[]) {
    return axios.post<ApiEndpoints<string>['/api/documents/reset']['POST']['response']>('/api/documents/reset', {
      depth,
      documentIds,
    });
  }

  async updateDocument(
    id: string,
    data: {
      category?: string | null;
      tags?: string[];
    },
  ) {
    return axios.patch<ApiEndpoints<string>['/api/documents/:id']['PATCH']['response']>(`/api/documents/${id}`, data);
  }

  async updateDocumentPage(
    id: string,
    data: {
      category?: string | null;
      tags?: string[];
    },
  ) {
    return axios.patch<ApiEndpoints<string>['/api/document-pages/:id']['PATCH']['response']>(
      `/api/document-pages/${id}`,
      data,
    );
  }

  async login(username: string, password: string) {
    const response = await axios.post<ApiEndpoints<string>['/api/auth/login']['POST']['response']>('/api/auth/login', {
      username,
      password,
    });

    localStorage.setItem('name', response.data.name);
    localStorage.setItem('role', response.data.role);
    this.setCsrfToken(response.data.csrfToken);
    return response;
  }

  async passkeysAvailable() {
    return axios.post<ApiEndpoints<string>['/api/auth/passkeys/available']['POST']['response']>(
      '/api/auth/passkeys/available',
      {},
    );
  }

  async passkeyAuthenticateOptions() {
    return axios.post<ApiEndpoints<string>['/api/auth/passkeys/authenticate/options']['POST']['response']>(
      '/api/auth/passkeys/authenticate/options',
      {},
    );
  }

  async passkeyAuthenticateVerify(credential: unknown) {
    const response = await axios.post<
      ApiEndpoints<string>['/api/auth/passkeys/authenticate/verify']['POST']['response']
    >('/api/auth/passkeys/authenticate/verify', {
      credential: credential as ApiEndpoints<
        string
      >['/api/auth/passkeys/authenticate/verify']['POST']['body']['credential'],
    });

    localStorage.setItem('name', response.data.name);
    localStorage.setItem('role', response.data.role);
    this.setCsrfToken(response.data.csrfToken);
    return response;
  }

  async passkeyRegisterOptions() {
    return axios.post<ApiEndpoints<string>['/api/auth/passkeys/register/options']['POST']['response']>(
      '/api/auth/passkeys/register/options',
      {},
    );
  }

  async passkeyRegisterVerify(credential: unknown) {
    return axios.post<ApiEndpoints<string>['/api/auth/passkeys/register/verify']['POST']['response']>(
      '/api/auth/passkeys/register/verify',
      {
        credential: credential as ApiEndpoints<
          string
        >['/api/auth/passkeys/register/verify']['POST']['body']['credential'],
      },
    );
  }

  async listPasskeys() {
    return axios.get<ApiEndpoints<string>['/api/auth/passkeys']['GET']['response']>('/api/auth/passkeys');
  }

  async deletePasskey(id: string) {
    return axios.delete<ApiEndpoints<string>['/api/auth/passkeys/:id']['DELETE']['response']>(
      `/api/auth/passkeys/${encodeURIComponent(id)}`,
    );
  }

  async logout() {
    await axios.post<ApiEndpoints<string>['/api/auth/logout']['POST']['response']>('/api/auth/logout');
    localStorage.removeItem('token');
    this.clearAuth();
  }

  async me() {
    return axios.get<ApiEndpoints<string>['/api/auth/me']['GET']['response']>('/api/auth/me');
  }

  async getDocumentsDistinctTags() {
    return axios.get<ApiEndpoints<string>['/api/documents/distinct-tags']['GET']['response']>(
      '/api/documents/distinct-tags',
    );
  }

  async updateBulkTags(documentIds: string[], tags: string[], operation: 'add' | 'remove') {
    return axios.post<ApiEndpoints<string>['/api/documents/patch-tags']['POST']['response']>('/api/documents/patch-tags', {
      documentIds,
      tags,
      operation,
    });
  }

  async retryDocumentPage(id: string, pageId: string, runner: string) {
    return axios.post<ApiEndpoints<string>['/api/documents/:id/pages/:pageId/retry']['POST']['response']>(
      `/api/documents/${id}/pages/${pageId}/retry`,
      { runner },
    );
  }

  async pageRetry(id: string, runner: string, extra?: { bypassHtmlCache?: boolean }) {
    return axios.post<ApiEndpoints<string>['/api/document-pages/:id/retry']['POST']['response']>(`/api/document-pages/${id}/retry`, {
      runner,
      ...(extra?.bypassHtmlCache === true ? { bypassHtmlCache: true } : {}),
    });
  }

  async extractPageAs(id: string, category: string) {
    return axios.post<ApiEndpoints<string>['/api/document-pages/:id/extract-as']['POST']['response']>(
      `/api/document-pages/${id}/extract-as`,
      { category },
    );
  }

  async flagDocumentPage(id: string, note?: string) {
    return axios.post<ApiEndpoints<string>['/api/document-pages/:id/flag']['POST']['response']>(
      `/api/document-pages/${id}/flag`,
      { note },
    );
  }

  async getCurationQueue(params: { source?: string; category?: string; limit?: number }) {
    return axios.get<ApiEndpoints<string>['/api/curation/queue']['GET']['response']>('/api/curation/queue', {
      params,
    });
  }

  async getCurationLeaderboard() {
    return axios.get<ApiEndpoints<string>['/api/curation/leaderboard']['GET']['response']>(
      '/api/curation/leaderboard',
    );
  }

  async postCurationReview(body: ApiEndpoints<string>['/api/curation/reviews']['POST']['body']) {
    return axios.post<ApiEndpoints<string>['/api/curation/reviews']['POST']['response']>(
      '/api/curation/reviews',
      body,
    );
  }

  async getCurationReviews(
    params: { reviewerId?: string; source?: string; category?: string; limit?: number } = {},
  ) {
    return axios.get<ApiEndpoints<string>['/api/curation/reviews']['GET']['response']>('/api/curation/reviews', {
      params,
    });
  }

  async getPageCurationReviews(id: string) {
    return axios.get<ApiEndpoints<string>['/api/document-pages/:id/curation-reviews']['GET']['response']>(
      `/api/document-pages/${id}/curation-reviews`,
    );
  }

  async getCurationSimilarPages(pageId: string) {
    return axios.get<ApiEndpoints<string>['/api/curation/similar-pages/:pageId']['GET']['response']>(
      `/api/curation/similar-pages/${encodeURIComponent(pageId)}`,
    );
  }

  async undoCurationReview(id: number) {
    return axios.post<ApiEndpoints<string>['/api/curation/reviews/:id/undo']['POST']['response']>(
      `/api/curation/reviews/${encodeURIComponent(id)}/undo`,
    );
  }

  async undoCurationReviewBatch(batchId: string) {
    return axios.post<ApiEndpoints<string>['/api/curation/reviews/undo-batch']['POST']['response']>(
      '/api/curation/reviews/undo-batch',
      { batchId },
    );
  }

  async listAuditLogs(
    page = 1,
    limit = 20,
    options?: {
      excludeUsernames?: string[];
      excludeActions?: string[];
      includeActions?: string[];
      dataFilter?: string;
    },
  ) {
    return axios.get<ApiEndpoints<string>['/api/audit-log']['GET']['response']>('/api/audit-log', {
      params: { page, limit, ...(options ?? {}) },
    });
  }

  async getAuditLogActivityChart(query?: { groupBy?: 'username' | 'action' }) {
    return axios.get<ApiEndpoints<string>['/api/audit-log/activity-chart']['GET']['response']>(
      '/api/audit-log/activity-chart',
      {
        params: {
          group_by: query?.groupBy,
        },
      },
    );
  }

  async listDocumentsByPage(pageId: string, page = 1, limit = 10) {
    return axios.get<ApiEndpoints<string>['/api/document-pages/:id/documents']['GET']['response']>(
      `/api/document-pages/${pageId}/documents`,
      {
        params: { page, limit },
      },
    );
  }

  async listReconstructedDocumentsByPage(pageId: string, page = 1, limit = 10) {
    return axios.get<ApiEndpoints<string>['/api/document-pages/:id/reconstructed-documents']['GET']['response']>(
      `/api/document-pages/${pageId}/reconstructed-documents`,
      {
        params: { page, limit },
      },
    );
  }

  async listCategories() {
    return axios.get<ApiEndpoints<string>['/api/categories']['GET']['response']>('/api/categories');
  }

  async getCategory(id: string) {
    return axios.get<ApiEndpoints<string>['/api/categories/:id']['GET']['response']>(`/api/categories/${id}`);
  }

  async trainCategory(id: string) {
    return axios.post<ApiEndpoints<string>['/api/categories/:id/train']['POST']['response']>(`/api/categories/${id}/train`);
  }

  async listSimilarPages(
    pageId: string,
    options?: {
      page?: number;
      limit?: number;
      projectId?: string;
    },
  ) {
    return axios.get<ApiEndpoints<string>['/api/document-pages/:id/similar-pages']['GET']['response']>(
      `/api/document-pages/${pageId}/similar-pages`,
      {
        params: { page: options?.page, limit: options?.limit, project_id: options?.projectId },
      },
    );
  }

  async reclassifyCategory(id: string) {
    return axios.post<ApiEndpoints<string>['/api/categories/:id/reclassify']['POST']['response']>(
      `/api/categories/${id}/reclassify`,
    );
  }

  async patchCategory(id: number, data: ApiEndpoints<string>['/api/categories/:id']['PATCH']['body']) {
    return axios.patch<ApiEndpoints<string>['/api/categories/:id']['PATCH']['response']>(`/api/categories/${id}`, data);
  }

  // Guarded server-side: 409 while pages are still labelled with the category, or when
  // its name is referenced in extraction code. See CategoryDeleteBlockedData.
  async deleteCategory(id: string) {
    return axios.delete<ApiEndpoints<string>['/api/categories/:id']['DELETE']['response']>(`/api/categories/${id}`);
  }

  async listCategoryClusters(filter: { categoryId: string }) {
    return axios
      .get<ApiEndpoints<string>['/api/category-clusters']['GET']['response']>('/api/category-clusters', {
        params: { category_id: filter.categoryId },
      })
      .then((res) => res.data);
  }

  async getCategoryClustersMap() {
    return axios
      .get<ApiEndpoints<string>['/api/category-clusters/map']['GET']['response']>(
        '/api/category-clusters/map',
      )
      .then((res) => res.data);
  }

  async getCategoryNeighborhood(categoryId: string) {
    return axios
      .get<ApiEndpoints<string>['/api/category-clusters/neighborhood']['GET']['response']>(
        '/api/category-clusters/neighborhood',
        { params: { category_id: categoryId } },
      )
      .then((res) => res.data);
  }

  async getCategoryClusterOutliers(categoryId: string) {
    return axios
      .get<ApiEndpoints<string>['/api/category-clusters/outliers']['GET']['response']>(
        '/api/category-clusters/outliers',
        { params: { category_id: categoryId } },
      )
      .then((res) => res.data);
  }

  async postCategoryRCEmbeddings(text: string, categoryId: number) {
    return axios.post<ApiEndpoints<string>['/api/categories/rc-embeddings']['POST']['response']>(
      '/api/categories/rc-embeddings',
      { text, categoryId },
    );
  }

  async deleteCategoryRCEmbeddings(id: number) {
    return axios.delete<ApiEndpoints<string>['/api/categories/rc-embeddings/:id']['DELETE']['response']>(
      `/api/categories/rc-embeddings/${id}`,
    );
  }

  async matchRCEmbeddings(text: string) {
    return axios.post<ApiEndpoints<string>['/api/categories/rc-embeddings/match']['POST']['response']>(
      '/api/categories/rc-embeddings/match',
      { text },
    );
  }

  async listUploads(options?: { page?: number; limit?: number }) {
    return axios.get<ApiEndpoints<string>['/api/uploads']['GET']['response']>('/api/uploads', {
      params: { page: options?.page, limit: options?.limit },
    });
  }

  async getUpload(id: string) {
    return axios.get<ApiEndpoints<string>['/api/uploads/:id']['GET']['response']>(`/api/uploads/${id}`);
  }

  async listSupertypes(options?: { page?: number; limit?: number }) {
    return axios.get<ApiEndpoints<string>['/api/supertypes']['GET']['response']>('/api/supertypes', {
      params: { page: options?.page, limit: options?.limit },
    });
  }

  async getSupertype(id: string) {
    return axios.get<ApiEndpoints<string>['/api/supertypes/:id']['GET']['response']>(`/api/supertypes/${id}`);
  }

  async patchSupertype(id: string, data: ApiEndpoints<string>['/api/supertypes/:id']['PATCH']['body']) {
    return axios.patch<ApiEndpoints<string>['/api/supertypes/:id']['PATCH']['response']>(`/api/supertypes/${id}`, data);
  }

  async postSupertype(data: ApiEndpoints<string>['/api/supertypes']['POST']['body']) {
    return axios.post<ApiEndpoints<string>['/api/supertypes']['POST']['response']>('/api/supertypes', data);
  }

  async deleteSupertype(id: string) {
    return axios.delete<ApiEndpoints<string>['/api/supertypes/:id']['DELETE']['response']>(`/api/supertypes/${id}`);
  }

  async go(query: string) {
    return axios.post<ApiEndpoints<string>['/api/go']['POST']['response']>('/api/go', { query });
  }

  async listProjects(options?: { page?: number; limit?: number }) {
    return axios.get<ApiEndpoints<string>['/api/projects']['GET']['response']>('/api/projects', {
      params: { page: options?.page, limit: options?.limit },
    });
  }

  /** Admin sweep of every project that still owes Oil at least one rollup family (DR-2855). */
  async listUndeliveredEvents(options?: { page?: number; limit?: number; reason?: ProjectUndeliveredReason }) {
    return axios.get<ApiEndpoints<string>['/api/projects/undelivered-events']['GET']['response']>(
      '/api/projects/undelivered-events',
      {
        params: {
          page: options?.page,
          limit: options?.limit,
          ...(options?.reason && { reason: options.reason }),
        },
      },
    );
  }

  // `coverage` goes out as a repeated parameter (`coverage=a&coverage=b`), not axios's
  // default `coverage[]=a`, which is the shape the agent-turns contract documents.
  async listAgentTurns(query: NonNullable<ApiEndpoints<string>['/api/agent-turns']['GET']['query']>) {
    return axios.get<ApiEndpoints<string>['/api/agent-turns']['GET']['response']>('/api/agent-turns', {
      params: query,
      paramsSerializer: { indexes: null },
    });
  }

  async listAgentTurnGaps(query: NonNullable<ApiEndpoints<string>['/api/agent-turns/gaps']['GET']['query']>) {
    return axios.get<ApiEndpoints<string>['/api/agent-turns/gaps']['GET']['response']>('/api/agent-turns/gaps', {
      params: query,
      paramsSerializer: { indexes: null },
    });
  }

  async getProject(id: string) {
    return axios.get<ApiEndpoints<string>['/api/projects/:id']['GET']['response']>(`/api/projects/${id}`);
  }

  async getProjectDocumentTypes(id: string) {
    return axios.get<ApiEndpoints<string>['/api/projects/:id/document-types']['GET']['response']>(
      `/api/projects/${id}/document-types`,
    );
  }

  async getProjectGraph(id: string) {
    return axios.get<ApiEndpoints<string>['/api/projects/:id/graph']['GET']['response']>(`/api/projects/${id}/graph`);
  }

  async getPartRecords(partKey: string) {
    return axios.get<ApiEndpoints<string>['/api/parts/:partKey']['GET']['response']>(
      `/api/parts/${encodeURIComponent(partKey)}`,
    );
  }

  async getProjectSupervisorFindings(id: string) {
    return axios.get<ApiEndpoints<string>['/api/projects/:id/supervisor/findings']['GET']['response']>(
      `/api/projects/${id}/supervisor/findings`,
    );
  }

  async applySupervisorFinding(projectId: string, findingId: string) {
    return axios.post<ApiEndpoints<string>['/api/projects/:id/supervisor/findings/:findingId/apply']['POST']['response']>(
      `/api/projects/${projectId}/supervisor/findings/${findingId}/apply`,
      undefined,
      SKIP_ERROR_TOAST,
    );
  }

  async revertSupervisorFinding(projectId: string, findingId: string) {
    return axios.post<ApiEndpoints<string>['/api/projects/:id/supervisor/findings/:findingId/revert']['POST']['response']>(
      `/api/projects/${projectId}/supervisor/findings/${findingId}/revert`,
      undefined,
      SKIP_ERROR_TOAST,
    );
  }

  async dismissSupervisorFinding(projectId: string, findingId: string, body: DismissSupervisorFindingRequest) {
    return axios.post<ApiEndpoints<string>['/api/projects/:id/supervisor/findings/:findingId/dismiss']['POST']['response']>(
      `/api/projects/${projectId}/supervisor/findings/${findingId}/dismiss`,
      body,
      SKIP_ERROR_TOAST,
    );
  }

  async restoreSupervisorFinding(projectId: string, findingId: string) {
    return axios.post<ApiEndpoints<string>['/api/projects/:id/supervisor/findings/:findingId/restore']['POST']['response']>(
      `/api/projects/${projectId}/supervisor/findings/${findingId}/restore`,
      undefined,
      SKIP_ERROR_TOAST,
    );
  }

  async getSupervisorPrecision() {
    return axios.get<ApiEndpoints<string>['/api/supervisor/precision']['GET']['response']>(
      '/api/supervisor/precision',
      SKIP_ERROR_TOAST,
    );
  }

  /** Phase 5 audit sample. Keeps the interceptor's error toast: without the sample the page has nothing to show. */
  async getSupervisorAuditSample(size: number, checkId?: string) {
    return axios.get<ApiEndpoints<string>['/api/supervisor/audit/sample']['GET']['response']>('/api/supervisor/audit/sample', {
      params: { size: String(size), ...(checkId ? { checkId } : {}) },
    });
  }

  /** Keeps the interceptor's error toast: a failed submit shows it. The page keeps the item and the form, except on a 404 (the finding is gone), where it moves on. */
  async putSupervisorAuditLabel(findingId: string, body: SupervisorAuditLabelRequest) {
    return axios.put<ApiEndpoints<string>['/api/supervisor/findings/:findingId/audit']['PUT']['response']>(
      `/api/supervisor/findings/${findingId}/audit`,
      body,
    );
  }

  async getProjectOpenItems(id: string, query?: { group?: ProjectOpenItemGroupKey }) {
    return axios.get<ApiEndpoints<string>['/api/projects/:id/open-items']['GET']['response']>(
      `/api/projects/${id}/open-items`,
      {
        params: {
          ...(query?.group && { group: query.group }),
        },
      },
    );
  }

  async getProjectDocumentTypeDocuments(id: string, classification: string) {
    return axios.get<ApiEndpoints<string>['/api/projects/:id/document-type-documents']['GET']['response']>(
      `/api/projects/${id}/document-type-documents`,
      { params: { classification } },
    );
  }

  async getWebhookDeliveries(
    id: string,
    query?: { limit?: number; offset?: number; eventType?: string; from?: string; to?: string },
  ) {
    return axios.get<ApiEndpoints<string>['/api/projects/:id/webhook-deliveries']['GET']['response']>(
      `/api/projects/${id}/webhook-deliveries`,
      {
        params: {
          ...(query?.limit != null && { limit: query.limit }),
          ...(query?.offset != null && { offset: query.offset }),
          ...(query?.eventType && { eventType: query.eventType }),
          ...(query?.from && { from: query.from }),
          ...(query?.to && { to: query.to }),
        },
      },
    );
  }

  async getWebhookDeliveryPayload(id: string, deliveryId: number) {
    return axios.get<
      ApiEndpoints<string>['/api/projects/:id/webhook-deliveries/:deliveryId/payload']['GET']['response']
    >(`/api/projects/${id}/webhook-deliveries/${deliveryId}/payload`);
  }

  async getWebhookDeliveryStats(
    id: string,
    query?: ApiEndpoints<string>['/api/projects/:id/webhook-deliveries/stats']['GET']['query'],
  ) {
    return axios.get<ApiEndpoints<string>['/api/projects/:id/webhook-deliveries/stats']['GET']['response']>(
      `/api/projects/${id}/webhook-deliveries/stats`,
      {
        params: {
          ...(query?.range && { range: query.range }),
          ...(query?.eventType && { eventType: query.eventType }),
          ...(query?.from && { from: query.from }),
          ...(query?.to && { to: query.to }),
          ...(query?.groupBy && { groupBy: query.groupBy }),
        },
      },
    );
  }

  async listOrganisations(options?: { page?: number; limit?: number; search?: string }) {
    return axios.get<ApiEndpoints<string>['/api/organisations']['GET']['response']>('/api/organisations', {
      params: {
        page: options?.page,
        limit: options?.limit,
        search: options?.search,
      },
    });
  }

  async getOrganisation(id: string) {
    return axios.get<ApiEndpoints<string>['/api/organisations/:id']['GET']['response']>(`/api/organisations/${id}`);
  }

  async finalizeProject(id: string) {
    return axios.post<ApiEndpoints<string>['/api/projects/:id/finalize']['POST']['response']>(`/api/projects/${id}/finalize`);
  }

  async recoverProject(id: string) {
    return axios.post<ApiEndpoints<string>['/api/projects/:id/recover']['POST']['response']>(
      `/api/projects/${id}/recover`,
    );
  }

  async getDocumentProjects(id: string) {
    return axios.get<ApiEndpoints<string>['/api/documents/:id/projects']['GET']['response']>(
      `/api/documents/${id}/projects`,
    );
  }

  async generateUploadLink(fileName: string, mimeType: string) {
    return axios.post<ApiEndpoints<string>['/api/documents/generate-upload-link']['POST']['response']>(
      '/api/documents/generate-upload-link',
      { fileName, mimeType },
    );
  }

  async getCategoryCluster(id: string) {
    return axios.get<ApiEndpoints<string>['/api/category-clusters/:id']['GET']['response']>(`/api/category-clusters/${id}`);
  }

  async getCategoryClusterNearest(id: string, limit?: number) {
    return axios
      .get<ApiEndpoints<string>['/api/category-clusters/:id/nearest']['GET']['response']>(
        `/api/category-clusters/${id}/nearest`,
        { params: { ...(limit != null && { limit: String(limit) }) } },
      )
      .then((res) => res.data);
  }

  async listUsers(opts?: { includeDeactivated?: boolean }) {
    const params = opts?.includeDeactivated ? { includeDeactivated: 'true' } : undefined;
    return axios.get<ApiEndpoints<string>['/api/users']['GET']['response']>('/api/users', { params });
  }

  async createUser(body: ApiEndpoints<string>['/api/users']['POST']['body']) {
    return axios.post<ApiEndpoints<string>['/api/users']['POST']['response']>('/api/users', body);
  }

  async updateUserRole(id: string, body: ApiEndpoints<string>['/api/users/:id/role']['PATCH']['body']) {
    return axios.patch<ApiEndpoints<string>['/api/users/:id/role']['PATCH']['response']>(
      `/api/users/${encodeURIComponent(id)}/role`,
      body,
    );
  }

  async resetUserPassword(id: string) {
    return axios.post<ApiEndpoints<string>['/api/users/:id/reset-password']['POST']['response']>(
      `/api/users/${encodeURIComponent(id)}/reset-password`,
    );
  }

  async deactivateUser(id: string) {
    return axios.post<ApiEndpoints<string>['/api/users/:id/deactivate']['POST']['response']>(
      `/api/users/${encodeURIComponent(id)}/deactivate`,
    );
  }

  async activateUser(id: string) {
    return axios.post<ApiEndpoints<string>['/api/users/:id/activate']['POST']['response']>(
      `/api/users/${encodeURIComponent(id)}/activate`,
    );
  }

  async deleteUser(id: string) {
    return axios.delete<ApiEndpoints<string>['/api/users/:id']['DELETE']['response']>(
      `/api/users/${encodeURIComponent(id)}`,
    );
  }

  async getModelHash() {
    return axios.get<ApiEndpoints<string>['/api/system/model-hash']['GET']['response']>(
      '/api/system/model-hash',
    );
  }

  async getModelVersions() {
    return axios.get<ApiEndpoints<string>['/api/system/model-versions']['GET']['response']>(
      '/api/system/model-versions',
    );
  }

  async exportModel() {
    return axios.post('/api/system/export-model', undefined, {
      responseType: 'blob',
    });
  }

  async importModel(file: File) {
    return axios.post<ApiEndpoints<string>['/api/system/import-model']['POST']['response']>(
      '/api/system/import-model',
      file,
      {
        headers: { 'Content-Type': 'application/zip' },
      },
    );
  }

  async grabForeignFiles() {
    return axios.get<{
      status: 'ok';
      documents: number;
      pages: number;
      seconds: number;
    }>('/api/system/grab-foreign-files');
  }
}

const backend = new Backend();
export default backend;
export type { DocumentSchema as Document };
